import { z } from 'zod';
import { safeParseApiResponse } from '../parseApiResponse';

/** Utilisateur API — champs optionnels (plusieurs formes selon endpoints). */
export const apiUserSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.union([z.string(), z.number()]).optional(),
    email: z.string().optional(),
    role: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    photo: z.string().nullable().optional(),
    shopId: z.union([z.string(), z.number(), z.null()]).optional(),
    customRole: z
      .object({
        isActive: z.boolean().optional(),
        permissions: z.array(z.string()).optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

export const authBundleSchema = z
  .object({
    access_token: z.string().min(1).optional(),
    accessToken: z.string().min(1).optional(),
    user: apiUserSchema.optional(),
  })
  .passthrough();

/** POST /users/login, POST /auth/google */
export const loginResponseSchema = z
  .object({
    statusCode: z.union([z.string(), z.number()]).optional(),
    status: z.union([z.string(), z.number()]).optional(),
    code: z.union([z.string(), z.number()]).optional(),
    success: z.boolean().optional(),
    message: z.string().optional(),
    error: z.string().optional(),
    access_token: z.string().optional(),
    accessToken: z.string().optional(),
    user: apiUserSchema.optional(),
    data: authBundleSchema.optional(),
    payload: authBundleSchema.optional(),
  })
  .passthrough();

/** GET /users/me — utilisateur courant */
export const profileResponseSchema = z.union([
  z
    .object({
      data: apiUserSchema.optional(),
      user: apiUserSchema.optional(),
    })
    .passthrough(),
  apiUserSchema,
]);

/**
 * Extrait token + user depuis une réponse login (validée ou brute).
 * @returns {{ accessToken: string|null, user: object|null, statusCode: string|number|null, success: boolean|null, message: string|null, raw: unknown }}
 */
export function normalizeLoginResponse(raw) {
  const payload =
    raw && typeof raw === 'object'
      ? raw.data ?? raw.payload ?? raw
      : {};

  const accessToken =
    payload.access_token ??
    payload.accessToken ??
    raw?.access_token ??
    raw?.accessToken ??
    null;

  const user = payload.user ?? raw?.user ?? null;

  const statusCode = raw?.statusCode ?? raw?.status ?? raw?.code ?? null;
  const success =
    typeof raw?.success === 'boolean'
      ? raw.success
      : accessToken && user
        ? true
        : null;

  return {
    accessToken: accessToken ? String(accessToken) : null,
    user,
    statusCode,
    success,
    message: raw?.message ?? raw?.error ?? null,
    raw,
  };
}

/**
 * Valide (Zod) puis normalise une réponse login.
 */
export function parseLoginResponse(raw) {
  safeParseApiResponse(loginResponseSchema, raw, { label: 'auth/login' });
  return normalizeLoginResponse(raw);
}

/**
 * Extrait l'utilisateur depuis GET /users/me (ou cache).
 */
export function normalizeProfileResponse(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const nested = raw.data;
  if (nested && typeof nested === 'object' && (nested.id || nested._id || nested.email)) {
    return nested;
  }
  if (raw.user && typeof raw.user === 'object') {
    return raw.user;
  }
  if (raw.id || raw._id || raw.email) {
    return raw;
  }
  return null;
}

export function parseProfileResponse(raw) {
  safeParseApiResponse(profileResponseSchema, raw, { label: 'auth/profile' });
  return normalizeProfileResponse(raw);
}
